import React, { useState, useEffect } from 'react';
import { Search, Edit2, Trash2, ArrowLeft, Building2, AlignLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { fetchDepartments, createDepartment, updateDepartment, deleteDepartment } from '../store/departmentSlice';
import { useConfirm } from '../context/ConfirmContext';

const getAdminId = () => {
  return Cookies.get('adminId');
};

const DepartmentMaster = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { departments, loading } = useSelector((state) => state.department);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [currentName, setCurrentName] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    dispatch(fetchDepartments(getAdminId()));
  }, [dispatch]);

  const handleSave = async () => {
    if (!currentName.trim()) return;
    const adminId = getAdminId();
    
    if (editingId) {
      await dispatch(updateDepartment({ id: editingId, name: currentName, description: currentDescription }));
    } else {
      await dispatch(createDepartment({ adminId, name: currentName, description: currentDescription }));
    }
    
    setCurrentName('');
    setCurrentDescription('');
    setEditingId(null);
  };

  const handleEdit = (dept) => {
    setEditingId(dept._id || dept.id);
    setCurrentName(dept.name || dept.departmentName || '');
    setCurrentDescription(dept.description || '');
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm('Are you sure you want to delete this department?');
    if (!isConfirmed) return;
    await dispatch(deleteDepartment(id));
  };

  const handleCancel = () => {
    setCurrentName('');
    setCurrentDescription('');
    setEditingId(null);
  };

  const filteredDepartments = (departments || []).filter(dept => {
    const searchString = `${dept.name || dept.departmentName || ''} ${dept.description || ''}`.toLowerCase();
    return searchString.includes(searchQuery.toLowerCase());
  });

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      
      {/* Header section */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, gap: 16 }}>
        <button 
          onClick={() => navigate('/masters')}
          style={{ 
            background: 'var(--input-bg)', 
            border: '1px solid var(--border-light)', 
            color: 'var(--text-primary)',
            padding: '8px 16px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          <ArrowLeft size={16} />
          All Masters
        </button>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Department Master</h1>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>Create, update and structure organizational departments, configure color tags and heads.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, flex: 1, flexWrap: 'wrap' }}>
        {/* Left Pane - Form */}
        <div className="dash-glass-card" style={{ flex: '1 1 350px', maxWidth: '100%', padding: 24, display: 'flex', flexDirection: 'column', gap: 24, height: 'fit-content' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
            {editingId ? 'Edit Department' : 'Create Department'}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Department Name *</label>
              <div style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                gap: 12,
                alignItems: 'center'
              }}>
                <Building2 size={16} color="var(--accent-primary)" />
                <input 
                  type="text"
                  placeholder="e.g. HR, IT Support"
                  value={currentName}
                  onChange={(e) => setCurrentName(e.target.value)}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    color: 'var(--text-primary)', 
                    fontSize: 14, 
                    width: '100%',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Description</label>
              <div style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start'
              }}>
                <AlignLeft size={16} color="var(--accent-primary)" style={{ marginTop: 2 }} />
                <textarea 
                  placeholder="Optional description"
                  value={currentDescription}
                  onChange={(e) => setCurrentDescription(e.target.value)}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    color: 'var(--text-primary)', 
                    fontSize: 14, 
                    width: '100%',
                    resize: 'none',
                    minHeight: 60,
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 'auto' }}>
            <button 
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '10px',
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                color: 'var(--text-primary)',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="btn-primary"
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {editingId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        {/* Right Pane - List */}
        <div className="dash-glass-card" style={{ flex: '2 1 400px', minWidth: 0, padding: 24, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'fit-content', maxHeight: 'calc(100vh - 160px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: 'var(--input-bg)', 
              border: '1px solid var(--border-light)', 
              borderRadius: 8, 
              padding: '8px 16px',
              width: '100%',
              maxWidth: 300,
              gap: 12
            }}>
              <Search size={16} color="var(--text-muted)" />
              <input 
                type="text" 
                placeholder="Search departments..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: 'var(--text-primary)',
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Registered Departments: <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>{departments?.length || 0}</span>
            </div>
          </div>

          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, paddingRight: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)', width: 80 }}>Index</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Department Name</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Description</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)', width: 100, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>Loading...</td>
                  </tr>
                ) : filteredDepartments.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No departments found.</td>
                  </tr>
                ) : (
                  filteredDepartments.map((dept, index) => (
                    <tr key={dept._id || dept.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 13 }}>#{index + 1}</td>
                      <td style={{ padding: '16px', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Building2 size={14} color="var(--accent-primary)" />
                          {dept.name || dept.departmentName}
                        </div>
                      </td>
                      <td style={{ padding: '16px', color: 'var(--text-secondary)', fontSize: 13 }}>
                        {dept.description || '-'}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button 
                            onClick={() => handleEdit(dept)}
                            style={{ 
                              background: 'var(--input-bg)', 
                              border: '1px solid var(--border-light)', 
                              padding: '6px', 
                              borderRadius: 4, 
                              color: 'var(--text-primary)',
                              cursor: 'pointer' 
                            }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            onClick={() => handleDelete(dept._id || dept.id)}
                            style={{ 
                              background: 'rgba(239, 68, 68, 0.1)', 
                              border: '1px solid rgba(239, 68, 68, 0.3)', 
                              padding: '6px', 
                              borderRadius: 4, 
                              color: '#EF4444',
                              cursor: 'pointer' 
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentMaster;
